import React from 'react';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { Icon, CollapsibleBlock, Button } from 'factor';
import { getPathNumber } from 'iqm-framework';

import { ServerErrorMessage } from 'components/ServerErrorMessage';
import { errorFieldsMapper } from 'constants/errorFieldsMapper';
import { groupMultiselectedValues, getTargetingTooltip } from 'utils/helpers';
import { blockStyles } from 'components/Block';
import { UINoteIcon } from 'components/UINoteIcon';
import { ExcludedOption, NestedOption, Option } from 'models/Option';
import { AppState } from 'models/Store';
import { CampaignInfoField } from 'models/CampaignInfoFields';
import { AuthState } from 'store/auth2/reducer';
import { ExistingCampaignData } from 'models/ExistingCampaign';
import { inventoryActions, SetInventoryField, SelectInventoryField } from 'store/inventory/actions';
import { InventoryState } from 'store/inventory/reducer';
import { applicationActions } from 'store/app/actions';
import { Chips } from 'components/Chips';
import { ErrorCreatingResponse } from 'models/Response';
import { ALL_EXPANDED, CollapseMode } from 'store/app/constants';

import { SelectInventoryGroupWrapper } from './SelectInventoryGroupWrapper';
import { PublishersSelectWrapper } from './PublishersSelectWrapper';

import styles from './styles.module.scss';

type Props = SetInventoryField &
  SelectInventoryField & {
    auth: AuthState;
    inventoryState: InventoryState;
    editableCampaign: ExistingCampaignData | null;
    selectedInventoryGroups: ExcludedOption[];
    selectedDealGroups: ExcludedOption[];
    submitted: boolean;
    selectedPublishers: Option[];
    publishers: Option[];
    resetError: () => void;
    errorCreating: ErrorCreatingResponse | null;
    isCollapseOpen?: boolean;
    onToggle?: (isOpened: boolean) => void;
    collapseMode?: CollapseMode;
  };

type Options = ExcludedOption[];

type State = {
  includedInventoryGroups: ExcludedOption[];
  excludedInventoryGroups: ExcludedOption[];
};

class InventoryBlockComponent extends React.PureComponent<Props, State> {
  popup: Window | null | undefined;

  constructor(props: Props) {
    super(props);
    const { selectedInventoryGroups } = props;
    this.state = {
      includedInventoryGroups: selectedInventoryGroups
        .filter((grp) => grp.included)
        .map(this.addGroupIcon),
      excludedInventoryGroups: selectedInventoryGroups
        .filter((grp) => grp.excluded)
        .map(this.addGroupIcon),
    };
  }

  componentDidUpdate(prevProps: Props) {
    const { editableCampaign, inventoryState, selectedInventoryGroups } = this.props;
    const { inventoryGroups: inventoryGroupsOptions, dealGroups: dealGroupsOptions } =
      inventoryState;

    if (editableCampaign) {
      if (
        (editableCampaign.whiteListedInventoryGroupIds ||
          editableCampaign.blackListedInventoryGroupIds) &&
        (editableCampaign !== prevProps.editableCampaign ||
          inventoryGroupsOptions.length !== prevProps.inventoryState.inventoryGroups.length)
      ) {
        const includedInventoryGroupIds = (editableCampaign.whiteListedInventoryGroupIds || '')
          .split(',')
          .map((i) => +i);
        const excludedInventoryGroupIds = (editableCampaign.blackListedInventoryGroupIds || '')
          .split(',')
          .map((i) => +i);
        this.handleChangeInStore(CampaignInfoField.inventoryGroups)([
          ...inventoryGroupsOptions
            .filter((o) => includedInventoryGroupIds.includes(o.value))
            .map((o) => ({ ...o, included: true })),
          ...inventoryGroupsOptions
            .filter((o) => excludedInventoryGroupIds.includes(o.value))
            .map((o) => ({ ...o, excluded: true })),
        ]);
      }

      if (
        editableCampaign.groupDealId &&
        (editableCampaign !== prevProps.editableCampaign ||
          dealGroupsOptions.length !== prevProps.inventoryState.dealGroups.length)
      ) {
        const dealGroupIds = editableCampaign.groupDealId.split(',').map((i) => +i);
        this.handleChangeInStore(CampaignInfoField.dealGroups)(
          dealGroupsOptions.filter((o) => dealGroupIds.includes(o.value)),
        );
      }
    }

    if (!isEqual(prevProps.selectedInventoryGroups, selectedInventoryGroups)) {
      this.prepareInventoryGroupsToDisplay(selectedInventoryGroups);
    }
  }

  addGroupIcon = (grp: Option) => ({ ...grp, icon: <Icon name="Group" /> });

  prepareInventoryGroupsToDisplay = (selectedInventoryGroups: ExcludedOption[]) => {
    this.setState({
      includedInventoryGroups: selectedInventoryGroups
        .filter((grp) => grp.included)
        .map(this.addGroupIcon),
      excludedInventoryGroups: selectedInventoryGroups
        .filter((grp) => grp.excluded)
        .map(this.addGroupIcon),
    });
  };

  handleChangeInStore = (field: string) => (value: Options) => {
    const { selectInventoryField, resetError } = this.props;
    resetError();
    selectInventoryField({
      key: field,
      value,
    });
  };

  handleSetDataInStore = (field: string) => (value: Options) => {
    const { setInventoryField } = this.props;

    setInventoryField({
      key: field,
      value,
    });
  };

  handleInventoryRemove =
    (field: CampaignInfoField.inventoryGroups | CampaignInfoField.dealGroups) =>
    (item: ExcludedOption) => {
      const { [field]: selectedItems } = this.props;
      const filteredInventoryGroups = selectedItems.filter(
        (grp: ExcludedOption) => grp.value !== item.value,
      );

      this.handleChangeInStore(field)(filteredInventoryGroups);
    };

  getHeaderObj = () => {
    const {
      selectedPublishers,
      publishers,
      selectedInventoryGroups,
      inventoryState,
      selectedDealGroups,
    } = this.props;
    return {
      title: (
        <>
          <Icon name="CampaignsInventory" className="block_title-icon" />
          Inventory
        </>
      ),
      summary: {
        'Publisher Category': groupMultiselectedValues(
          selectedPublishers.filter(
            (i) => !i.uncountable && !publishers.find((val) => val.value === i.value),
          ),
          publishers.reduce((acc: Option<number>[], group: NestedOption): Option<number>[] => {
            if (group.options) {
              acc.push(...group.options);
            }
            return acc;
          }, []).length,
        ),
        'Inventory Allowlist': groupMultiselectedValues(
          selectedInventoryGroups.filter((i) => i.included),
          inventoryState.inventoryGroups.length,
        ),
        'Inventory Blocklist': groupMultiselectedValues(
          selectedInventoryGroups.filter((i) => i.excluded),
          inventoryState.inventoryGroups.length,
        ),
        'Deal Group': groupMultiselectedValues(
          selectedDealGroups,
          inventoryState.dealGroups.length,
        ),
      },
      keyIcon: {
        'Inventory Allowlist': (
          <UINoteIcon inline text={getTargetingTooltip('targeted', 'inventories')} marginLeft={0} />
        ),
        'Inventory Blocklist': (
          <UINoteIcon inline text={getTargetingTooltip('blocked', 'inventories')} marginLeft={0} />
        ),
      },
    };
  };

  onOpenInventoryGroup = () => {
    const { auth } = this.props;
    if (!auth.userData) {
      return;
    }
    this.popup = window.open(`/inventory/u/${getPathNumber()}/#/?action=newInventoryGroup`);
  };

  render() {
    const {
      selectedInventoryGroups,
      selectedDealGroups,
      auth,
      submitted,
      selectedPublishers,
      errorCreating,
      isCollapseOpen,
      onToggle,
      collapseMode,
    } = this.props;

    const { includedInventoryGroups, excludedInventoryGroups } = this.state;

    if (!auth.authorized) {
      return null;
    }

    return (
      <CollapsibleBlock
        collapsible={collapseMode !== ALL_EXPANDED}
        isCollapseOpen={isCollapseOpen}
        onToggle={onToggle}
        header={this.getHeaderObj()}
        customStyle={{
          block: `${blockStyles.block} ${!isCollapseOpen ? blockStyles.collapsed : ''}`,
          panelHeaderExpand: blockStyles.panelHeaderExpand,
          panelHeaderCollapse: blockStyles.panelHeaderCollapse,
        }}
      >
        <div
          data-qa="69"
          className={`row ${blockStyles.blockRow} ${
            !selectedInventoryGroups.length ? styles.inventoryBlock : ''
          }`}
        >
          <div data-qa="247" className="col-4" data-not-valid={!selectedPublishers.length}>
            <PublishersSelectWrapper
              className={submitted && !selectedPublishers.length ? '_error' : ''}
            />
            {submitted && !selectedPublishers.length ? (
              <p className="text-field__help-text _error">Publisher category must be selected!</p>
            ) : null}
            <ServerErrorMessage errorKey={errorFieldsMapper.selectedPublishers} />
          </div>
          <div data-qa="70" className={`col-4 ${styles.inventorySelectWrapper}`}>
            <SelectInventoryGroupWrapper
              placeholder="Select Inventory Group"
              label="Inventory Group"
              value={selectedInventoryGroups}
              className={
                errorCreating &&
                [
                  errorFieldsMapper.whiteListedInventoryGroupIds,
                  errorFieldsMapper.blackListedInventoryGroupIds,
                ].includes(errorCreating.errorField)
                  ? '_error'
                  : ''
              }
              onChange={this.handleChangeInStore(CampaignInfoField.inventoryGroups)}
              fetchedData={this.handleSetDataInStore('inventoryGroups')}
            />
            <ServerErrorMessage errorKey={errorFieldsMapper.blackListedInventoryGroupIds} />
            <ServerErrorMessage errorKey={errorFieldsMapper.whiteListedInventoryGroupIds} />
          </div>
          <div data-qa="71" className="col-4 d-flex align-items-center">
            <Button
              className="btn-square _cornflower-blue _filled _map m-0 _md"
              onClick={() => this.onOpenInventoryGroup()}
            >
              + Add new Inventory Group
            </Button>
            <span className={styles.createInventoryGroupTitle}>
              Create a new Inventory Group from Inventory App
            </span>
          </div>
        </div>
        <div data-qa="72" className={`${blockStyles.blockRow} align-items-start`}>
          <div className="col-4" />
          <div className={`col-4 mb-0 ${blockStyles.blockRow}`}>
            <Chips
              chipClassName="_include"
              items={includedInventoryGroups}
              onRemove={this.handleInventoryRemove(CampaignInfoField.inventoryGroups)}
            />
            <Chips
              chipClassName="_exclude"
              items={excludedInventoryGroups}
              onRemove={this.handleInventoryRemove(CampaignInfoField.inventoryGroups)}
            />
          </div>
          <div className={`col-4 mb-0 ${blockStyles.blockRow}`}>
            <Chips
              chipClassName="_include"
              items={selectedDealGroups}
              onRemove={this.handleInventoryRemove(CampaignInfoField.dealGroups)}
            />
          </div>
        </div>
      </CollapsibleBlock>
    );
  }
}

const mapState = (state: AppState) => ({
  auth: state.auth,
  submitted: state.app.submitted,
  editableCampaign: state.app.editableCampaign,
  errorCreating: state.app.errorCreating,
  inventoryState: state.inventory,
  selectedInventoryGroups: state.inventory.sidebarCampaignInfo[CampaignInfoField.inventoryGroups],
  selectedDealGroups: state.inventory.sidebarCampaignInfo[CampaignInfoField.dealGroups],
  selectedPublishers: state.publishers.selectedPublishers,
  publishers: state.publishers.publishers,
});

const mapAction = {
  resetError: applicationActions.resetError,
  setInventoryField: inventoryActions.setInventoryField,
  selectInventoryField: inventoryActions.selectInventoryField,
};

export const InventoryBlock = connect(mapState, mapAction)(InventoryBlockComponent);
